import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import EvaluationDetailModal from "@/components/app/EvaluationDetailModal";
import DateField from "@/components/app/DateField";
import {
  Plus, ChevronDown, ChevronRight, X, ClipboardCheck, Trash2, Save,
  TrendingUp, User as UserIcon, Layers,
} from "lucide-react";

const RATING_LABELS = ["", "Nunca", "A veces", "A menudo", "Casi siempre", "Siempre"];  // 1..5 (index 0 unused)

export default function EvaluationsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [items, setItems] = useState([]);
  const [filterTeam, setFilterTeam] = useState("");
  const [filterPlayer, setFilterPlayer] = useState(searchParams.get("player") || "");
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null); // an evaluation being viewed

  const canWrite = ["club_admin", "coordinator", "director_deportivo", "coach", "delegado"].includes(user?.role);

  const load = async () => {
    try {
      const [t, c, p] = await Promise.all([api.get("/teams"), api.get("/categories"), api.get("/players")]);
      setTeams(t.data); setCats(c.data); setPlayers(p.data);
      // If arrived with ?player=X, auto-select the player's team as filter
      if (filterPlayer && !filterTeam) {
        const pl = p.data.find(x => x.id === filterPlayer);
        if (pl?.team_id) setFilterTeam(pl.team_id);
      }
      const params = new URLSearchParams();
      if (filterTeam) params.set("team_id", filterTeam);
      if (filterPlayer) params.set("player_id", filterPlayer);
      const { data } = await api.get(`/evaluations?${params}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filterTeam, filterPlayer]);

  // Keep URL in sync so the panel is shareable / bidirectional link works.
  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (filterPlayer) p.set("player", filterPlayer); else p.delete("player");
    setSearchParams(p, { replace: true });
  }, [filterPlayer]);

  const playerName = (pid) => players.find(p => p.id === pid)?.name || "—";
  const teamName = (tid) => teams.find(t => t.id === tid)?.name || "—";

  const avgRating = (ratings) => {
    // Include rating=0 (valid "Nunca") but skip entries that never touched rating.
    const nums = (ratings || [])
      .filter(r => r.rating !== undefined && r.rating !== null)
      .map(r => Number(r.rating));
    if (!nums.length) return "—";
    return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta evaluación?")) return;
    try { await api.delete(`/evaluations/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline">Seguimiento individual</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Evaluación</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            Evalúa el desarrollo de cada jugador con la frecuencia que decidas (trimestral, mensual o libre).
            No sustituye a la jornada — es un seguimiento aparte.
          </p>
        </div>
        {canWrite && (
          <button data-testid="new-eval-btn" onClick={() => setShowForm(true)} className="btn-volt">
            <Plus size={16} /> Nueva evaluación
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div>
          <label className="overline block mb-1">Equipo</label>
          <select data-testid="filter-team" value={filterTeam} onChange={(e) => { setFilterTeam(e.target.value); setFilterPlayer(""); }} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
            <option value="">Todos</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Jugador</label>
          <select data-testid="filter-player" value={filterPlayer} onChange={(e) => setFilterPlayer(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
            <option value="">Todos</option>
            {players.filter(p => !filterTeam || p.team_id === filterTeam).map(p => (
              <option key={p.id} value={p.id}>{p.name} {p.jersey_number ? `#${p.jersey_number}` : ""}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {items.map((ev) => (
          <div key={ev.id} data-testid={`eval-item-${ev.id}`} className="border border-border p-5 hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 border border-border flex items-center justify-center shrink-0">
                  <ClipboardCheck size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="overline">Evaluación</span>
                    <span className="text-[10px] font-mono text-muted-foreground">· {teamName(ev.team_id)}</span>
                    <span className="text-[10px] font-mono text-primary">· {ev.date}</span>
                  </div>
                  <div className="font-heading font-bold text-lg uppercase tracking-tight">{playerName(ev.player_id)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Media: <span className="text-primary font-mono">{avgRating(ev.ratings)}/5</span>
                    {ev.objectives && <span className="ml-3">· Objetivos: {ev.objectives.slice(0, 80)}{ev.objectives.length > 80 ? "…" : ""}</span>}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-2">
                    Por {ev.author_name} · {ev.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`view-eval-${ev.id}`} onClick={() => setViewing(ev)} className="text-xs uppercase font-heading tracking-wider text-primary hover:underline">Ver</button>
                {(canWrite && (user?.role !== "coach" && user?.role !== "delegado" || ev.author_id === user?.id)) && (
                  <button data-testid={`del-eval-${ev.id}`} onClick={() => remove(ev.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-16 border border-dashed border-border">
            <ClipboardCheck className="mx-auto text-muted-foreground" size={28} />
            <div className="mt-3 text-muted-foreground text-sm">Aún no hay evaluaciones registradas.</div>
            {canWrite && <button onClick={() => setShowForm(true)} className="mt-4 text-xs uppercase font-heading tracking-wider text-primary hover:underline">Crear la primera</button>}
          </div>
        )}
      </div>

      {showForm && <EvaluationForm teams={teams} players={players} cats={cats} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {viewing && (
        <EvaluationDetailModal
          evaluation={viewing}
          playerName={playerName(viewing.player_id)}
          teamName={teamName(viewing.team_id)}
          playerPosition={players.find(p => p.id === viewing.player_id)?.position}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function EvaluationForm({ teams, players, cats, onClose, onSaved }) {
  const [teamId, setTeamId] = useState(teams[0]?.id || "");
  const [playerId, setPlayerId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [template, setTemplate] = useState(null);
  const [porteroTpl, setPorteroTpl] = useState(null);
  const [ratings, setRatings] = useState({}); // {indicator_id: {rating, intention, success}}
  const [objectives, setObjectives] = useState("");
  const [notes, setNotes] = useState("");
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);

  const player = useMemo(() => players.find(p => p.id === playerId), [players, playerId]);
  const activeTpl = useMemo(() => player?.position === "POR" ? porteroTpl : template, [player, template, porteroTpl]);

  useEffect(() => {
    if (!teamId) return;
    (async () => {
      const t = teams.find(x => x.id === teamId);
      if (!t) return;
      const cat = cats.find(c => c.id === t.category_id);
      const tplKey = cat?.template_key || "U12";
      try {
        const [tpl, tplPor] = await Promise.all([
          api.get(`/templates/${tplKey}?team_id=${teamId}`),
          api.get(`/templates/PORTERO?team_id=${teamId}`),
        ]);
        setTemplate(tpl.data); setPorteroTpl(tplPor.data);
        const exp = {};
        (tpl.data.blocks || []).forEach((b, i) => { exp[b.id] = i === 0; });
        setExpanded(exp);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [teamId, teams, cats]);

  const teamPlayers = players.filter(p => p.team_id === teamId);
  useEffect(() => { if (!playerId && teamPlayers[0]) setPlayerId(teamPlayers[0].id); }, [teamId]);

  const setRating = (indId, field, value) => {
    setRatings(prev => ({ ...prev, [indId]: { ...(prev[indId] || {}), [field]: value } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!playerId) { toast.error("Selecciona un jugador"); return; }
    const payload = {
      player_id: playerId,
      date,
      ratings: Object.entries(ratings)
        // Keep entries where user actually touched the rating (0 is valid) OR toggled intention/success.
        .filter(([, v]) => (v.rating !== undefined && v.rating !== null) || v.intention !== undefined || v.success !== undefined)
        .map(([indId, v]) => ({
          indicator_id: indId,
          rating: (v.rating !== undefined && v.rating !== null) ? Number(v.rating) : 0,
          intention: v.intention ?? null,
          success: v.success ?? null,
        })),
      objectives: objectives || null,
      notes: notes || null,
    };
    setSaving(true);
    try {
      await api.post("/evaluations", payload);
      toast.success("Evaluación guardada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} data-testid="eval-form" className="bg-card border border-border w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Nueva</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">Evaluación</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:text-primary"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="overline block mb-1">Equipo *</label>
            <select data-testid="eval-team" required value={teamId} onChange={(e) => { setTeamId(e.target.value); setPlayerId(""); setRatings({}); }} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Jugador *</label>
            <select data-testid="eval-player" required value={playerId} onChange={(e) => setPlayerId(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              <option value="">— Selecciona —</option>
              {teamPlayers.map(p => <option key={p.id} value={p.id}>{p.name} {p.jersey_number ? `#${p.jersey_number}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Fecha *</label>
            <DateField value={date} onChange={setDate} testid="eval-date" />
          </div>
        </div>

        {activeTpl?.blocks?.map((b) => (
          <div key={b.id} className="border border-border">
            <button type="button" onClick={() => setExpanded(x => ({ ...x, [b.id]: !x[b.id] }))} className="w-full flex items-center gap-2 p-3 hover:bg-white/[0.02] text-left">
              {expanded[b.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="font-heading font-bold uppercase tracking-tight text-sm">{b.name}</span>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{(b.indicators || []).length} indicadores</span>
            </button>
            {expanded[b.id] && (
              <div className="border-t border-border p-3 space-y-3">
                {(b.indicators || []).map((ind) => (
                  <div key={ind.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                    <div className="md:col-span-6 text-sm">{ind.name}</div>
                    <div className="md:col-span-3 flex gap-1" data-testid={`eval-rating-${ind.id}`}>
                      {[1, 2, 3, 4, 5].map((v) => (
                        <button key={v} type="button" onClick={() => setRating(ind.id, "rating", v)}
                          title={RATING_LABELS[v]}
                          className={`w-8 h-8 border text-xs font-mono ${ratings[ind.id]?.rating === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                    {ind.has_int && (
                      <div className="md:col-span-1 flex items-center justify-center">
                        <input type="checkbox" title="Intento (¿lo intentó?)" checked={!!ratings[ind.id]?.intention} onChange={(e) => setRating(ind.id, "intention", e.target.checked)} className="accent-primary" />
                      </div>
                    )}
                    {ind.has_sn && (
                      <div className="md:col-span-1 flex items-center justify-center">
                        <input type="checkbox" title="Éxito (¿lo consiguió?)" checked={!!ratings[ind.id]?.success} onChange={(e) => setRating(ind.id, "success", e.target.checked)} className="accent-primary" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div>
          <label className="overline block mb-1">Objetivos para el próximo periodo</label>
          <textarea data-testid="eval-objectives" rows={3} value={objectives} onChange={(e) => setObjectives(e.target.value)} maxLength={500} placeholder="Ej: mejorar el pase largo, más agresividad en defensa…" className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="overline block mb-1">Observaciones (opcional)</label>
          <textarea data-testid="eval-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
          <button type="submit" data-testid="submit-eval-btn" disabled={saving} className="btn-volt disabled:opacity-50"><Save size={14} /> {saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}
